import { useState } from 'react';
import { getWorkspace, updateConsent } from '../lib/store';
import type { WorkspaceConsent } from '../lib/types';

interface Props {
  onProceed: () => void;
  onLocalOnly: () => void;
}

export function ConsentGate({ onProceed, onLocalOnly }: Props) {
  const ws = getWorkspace();
  const [c, setC] = useState<WorkspaceConsent>({
    ...ws.consent,
    allow_server_storage: false,
    allow_analytics: false,
    allow_followups: false,
  });

  const [retention, setRetention] = useState(365);

  const allSelected = c.allow_server_storage; // minimum for "cloud" features

  function handleProceed() {
    updateConsent({
      ...c,
      retention_days: retention,
      consent_granted_at: Date.now(),
    });
    onProceed();
  }

  return (
    <div className="max-w-2xl mx-auto px-5 py-12">
      <div className="mb-8">
        <div className="uppercase tracking-[3px] text-[10px] text-[var(--text-subtle)] mb-2">PRIVATE INSTRUMENT</div>
        <h1 className="text-4xl font-medium tracking-tight text-[var(--text)] mb-3">Before we begin</h1>
        <p className="text-lg text-[var(--text-muted)] leading-snug">
          This is a space for rehearsing the fear of being wrong in public — especially when you are already depleted.
          Nothing is designed to pull more attention from you than you freely give.
        </p>
      </div>

      <div className="space-y-8 text-[15px] leading-relaxed">
        <div className="rounded-2xl border border-[var(--border)] bg-[var(--bg-elev)] p-6">
          <div className="font-medium mb-3 text-[var(--text)]">What we capture when you choose to store a rehearsal</div>
          <ul className="space-y-2 text-[var(--text-muted)] list-disc pl-5">
            <li>Your written gratitude and the situation you rehearsed (raw text)</li>
            <li>The theme of the rumination loop you named</li>
            <li>Before/after emotional residue ratings (shame, anxiety, fatigue, self-doubt)</li>
            <li>How successful the reappraisal felt in the moment</li>
            <li>How often the loop visits you</li>
            <li>Your chosen better question, choice, or repair</li>
            <li>Timestamps and (optionally) how long you spent in the rehearsal</li>
          </ul>
        </div>

        <div>
          <div className="font-medium mb-2 text-[var(--text)]">Why we ask for these</div>
          <p className="text-[var(--text-muted)]">
            So you can later see your own <span className="text-[var(--text)]">ambiguity tolerance curve</span> — a private record of whether sitting with the fear changes anything for you over weeks and months.
            Optional follow-up prompts (created by Workflows) let you notice if the residue or loop frequency actually shifts days later. All of this stays keyed only to a workspace ID you control.
          </p>
        </div>

        <div className="border-l-2 border-[var(--accent)] pl-4 text-[var(--text-muted)]">
          You can use the entire rehearsal and see a local curve with zero data leaving your device.
          Cloud features (persistent curve across devices, check-ins, nothing else) require explicit opt-in below.
        </div>

        <div className="space-y-4 pt-2">
          <label className="flex gap-3 items-start cursor-pointer">
            <input
              type="checkbox"
              checked={c.allow_server_storage}
              onChange={(e) => setC({ ...c, allow_server_storage: e.target.checked })}
              className="mt-1 accent-[var(--accent)]"
            />
            <span>
              <span className="font-medium text-[var(--text)]">Store my rehearsals on the server</span> so I can see my curve over time and access this workspace from other devices. I understand the content will be stored in Cloudflare D1 under my workspace ID only.
            </span>
          </label>

          <label className="flex gap-3 items-start cursor-pointer">
            <input
              type="checkbox"
              checked={c.allow_followups}
              onChange={(e) => setC({ ...c, allow_followups: e.target.checked })}
              disabled={!c.allow_server_storage}
              className="mt-1 accent-[var(--accent)] disabled:opacity-40"
            />
            <span className={c.allow_server_storage ? '' : 'opacity-50'}>
              Create <span className="font-medium text-[var(--text)]">private follow-up prompts</span> (via Workflows) a few days and weeks after a rehearsal. I will only see them when I open this app. No email, no push, no reminders.
            </span>
          </label>

          <label className="flex gap-3 items-start cursor-pointer">
            <input
              type="checkbox"
              checked={c.allow_analytics}
              onChange={(e) => setC({ ...c, allow_analytics: e.target.checked })}
              className="mt-1 accent-[var(--accent)]"
            />
            <span>
              Let the app send coarse, <span className="font-medium text-[var(--text)]">anonymous usage signals</span> (e.g. “rehearsal completed”, “check-in filled”) to Cloudflare Analytics Engine. No text content, no themes that could identify me. Used only to understand whether the instrument itself is respectful of attention.
            </span>
          </label>
        </div>

        <div>
          <div className="text-sm text-[var(--text-muted)] mb-1.5">Data retention (if server storage enabled)</div>
          <select
            value={retention}
            onChange={(e) => setRetention(parseInt(e.target.value))}
            className="bg-[var(--bg-elev)] border border-[var(--border)] rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-[var(--accent)]"
          >
            <option value={90}>90 days — then auto-delete</option>
            <option value={365}>1 year</option>
            <option value={1825}>5 years</option>
            <option value={99999}>Keep until I delete</option>
          </select>
        </div>
      </div>

      <div className="mt-10 flex flex-col sm:flex-row gap-3">
        <button
          onClick={handleProceed}
          disabled={!allSelected}
          className="flex-1 rounded-2xl bg-[var(--accent)] text-white py-3.5 text-[15px] font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-105 active:brightness-95 transition"
        >
          Save with these choices and continue
        </button>
        <button
          onClick={() => {
            // local only, still record minimal consent so we don't nag
            updateConsent({
              allow_server_storage: false,
              allow_analytics: false,
              allow_followups: false,
              retention_days: 0,
            });
            onLocalOnly();
            // still allow proceeding with local
            onProceed();
          }}
          className="flex-1 rounded-2xl border border-[var(--border)] py-3.5 text-[15px] hover:bg-[var(--bg-elev)] transition"
        >
          Use completely locally (no server storage)
        </button>
      </div>

      <p className="text-center text-xs text-[var(--text-subtle)] mt-6 max-w-prose mx-auto">
        You can change any of this later in Settings. Full export and one-click deletion of everything are always available.
        Read the full privacy approach on the Privacy page.
      </p>
    </div>
  );
}
